import React, { Fragment, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useExpanded, usePagination, useTable, useFilters, useRowSelect, useBlockLayout } from "react-table";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Pagination from "@mui/material/Pagination";

import DialogRowError from "./DialogRowError";
import { resetDrmTableFilter } from "../../redux/reducers/drmTableFilterSlice";
import { resetDrmTableFilter as resetDrmDailyTableFilter } from "../../redux/reducers/drmDailyTableFilterSlice";
import { resetCoupaTableFilter } from "../../redux/reducers/drmCoupaTableFilterSlice";
import { setSelectedRows } from "../../redux/reducers/drmTableDataSlice";
import { EditableCell } from "../tmt/EditableCell";
import { useUpdateTableData } from "../../customHook/useUpdateTableData ";
import { defaultFilter, pageSize } from "../../helpers";

import clsx from "clsx";
import "./DrmTable.sass";

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <Checkbox inputRef={resolvedRef} indeterminate={indeterminate} {...rest} />;
});

const SelectionHeader = ({ allowRowSelection, getToggleAllRowsSelectedProps }) => (
  <>
    {allowRowSelection && (
      <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
    )}
  </>
);

const SelectionCell = ({ allowRowSelection, row }) => (
  <>
    {allowRowSelection && (
      <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
    )}
  </>
);

const SelectionFilter = ({ onClear, label }) => (
  <Link
    underline="always"
    onClick={onClear}
    className="drmt-filter-link"
    data-testid="clear-all-filters-link"
  >
    {label}
  </Link>
);

function DrmTable({ tableName, tableState, columnOrder, setTableState, tableData, editableRow, onPage, totalPages, totalRecords, isLoadingDrmTable, handleRefresh, handleClear, allowRowSelection = editableRow, onSelectionChange }) {
  const onPageRef = useRef(onPage);
  const pageSizeRef = useRef(pageSize);
  const totalPagesRef = useRef(totalPages);
  const totalRecordsRef = useRef(totalRecords);
  const { updateData } = useUpdateTableData({ onPageRef, pageSizeRef, totalPagesRef, totalRecordsRef, tableData });

  const { isOpenMenu } = useSelector((states) => states?.leftMenu);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [dialog, setDialog] = React.useState({ isOpen: false, errors: [] });
  const [startRecNum, setStartRecNum] = useState(0);
  const [endRecNum, setEndRecNum] = useState(0);
  
  const tableTypeMap = {
    Bid: "drmBidTableData",
    Daily: "drmDailyTableData",
  };
  const tableType = tableTypeMap[tableName] || null;
  const selectedRowsFromRedux = useSelector((state) =>
    tableType ? state?.drmTableData?.[tableType]?.selectedRows || [] : []
  );

  useEffect(() => {
    onPageRef.current = onPage;
    pageSizeRef.current = pageSize;
    totalPagesRef.current = totalPages;
    totalRecordsRef.current = totalRecords;
  }, [onPage, pageSize, totalPages, totalRecords]);

  const renderCell = useCallback((cellProps) => {
    if (!editableRow) {
      const headerLabel = cellProps.column?.Header || cellProps.column?.id || "cell";
      const displayValue = cellProps.value ?? "-";
      return <div data-testid={`${headerLabel}-${displayValue}`}>{displayValue}</div>;
    }
    return <EditableCell {...cellProps} updateData={updateData} setDialog={setDialog} />;
  }, [editableRow, updateData]);

  const defaultColumn = useMemo(() => ({
    Cell: renderCell,
  }), [renderCell]);

  const setAllFiltersRef = useRef(null);

  const handleClearAllFilters = useCallback(() => {
    if (setAllFiltersRef.current) {
      setAllFiltersRef.current([]);
    }
    if (tableName === "Bid") {
      dispatch(resetDrmTableFilter());
    } else if (tableName === "Daily") {
      dispatch(resetDrmDailyTableFilter());
    } else if (tableName === "Coupa") {
      dispatch(resetCoupaTableFilter());
    }
    handleClear();
  }, [handleClear, tableName]);

  const tableInstance = useTable({
    columns: columnOrder,
    data: tableData,
    defaultColumn,
    initialState: { pageIndex: onPage - 1, pageSize },
    manualPagination: true,
    pageCount: totalPages,
    autoResetSelectedRows: false,
    autoResetPage: false,
    autoResetFilters: false,
    autoResetSortBy: false,
    autoResetExpanded: false,
  }, useFilters, useExpanded, usePagination, useRowSelect, useBlockLayout, (hooks) => {
    hooks.visibleColumns.push((columns) => [
       {
        id: "selection",
        Header: (headerProps) => (
          <SelectionHeader
            allowRowSelection={allowRowSelection}
            {...headerProps}
          />
        ),
        Cell: ({ row }) => (
          <SelectionCell
            allowRowSelection={allowRowSelection}
            row={row}
          />
        ),
        Filter: () => (
          <SelectionFilter
            onClear={handleClearAllFilters}
            label={t("clear_all")}
          />
        ),
      },
      ...columns,
    ]);
  });

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, gotoPage, visibleColumns, selectedFlatRows, allColumns, getToggleHideAllColumnsProps, setAllFilters, toggleAllRowsSelected } = tableInstance;

  useEffect(() => {
    setAllFiltersRef.current = setAllFilters;
  }, [setAllFilters]);

  const headersInitialized = useRef(false);

  useEffect(() => {
    if (!headersInitialized.current || allColumns) {
      setTableState((preValue) => ({ ...preValue, headers: allColumns, toggleProps: getToggleHideAllColumnsProps }));
      headersInitialized.current = true;
    }
  }, [setTableState, tableState.headers, allColumns, headerGroups]);

  const selectedRowsDataRef = useRef([]);
  
  useEffect(() => {
    const selectedRowsData = selectedFlatRows.map(row => ({ 
      original: row.original,
      index: row.index,
      id: row.id
    }));
    
    selectedRowsDataRef.current = selectedRowsData;
    if (typeof onSelectionChange === "function") {
      onSelectionChange(selectedRowsData);
    }
  }, [selectedFlatRows, onSelectionChange]);
  
  useEffect(() => {
    if (!tableType) {
      return () => {};
    }

    const timer = setTimeout(() => {
      dispatch(
        setSelectedRows({
          tableType,
          selectedRows: selectedRowsDataRef.current,
        })
      );
    }, 0);

    return () => clearTimeout(timer);
  }, [selectedFlatRows.length, tableType]);

  const handlePageChange = useCallback((event, value) => {
    setTableState((preValue) => ({ ...preValue, currentPage: value }));
    gotoPage(value - 1);
    handleRefresh(value, defaultFilter);
  }, [setTableState, gotoPage, handleRefresh]);

  const handleCloseErrorDialog = useCallback(() => {
    setDialog({ isOpen: false, errors: [] });
  }, []);

  useEffect(() => {
    if (!isLoadingDrmTable) {
      toggleAllRowsSelected(false);
      if (tableData?.length === 0) {
        setStartRecNum(0);
        setEndRecNum(0);
      } else if (onPage > 1) {
        let num = onPage - 1;
        setStartRecNum(num * pageSize + 1);
        setEndRecNum(num * pageSize + tableData?.length);
      } else if (onPage === 1 || tableData?.length) {
        setStartRecNum(1);
        setEndRecNum(tableData?.length);
      }
    }
  }, [tableData?.length, isLoadingDrmTable, onPage, pageSize]);

  const totalRecord = totalRecordsRef.current || 0;
  const computedSelectedCount = tableType ? selectedRowsFromRedux.length : selectedFlatRows.length;

  return (
    <Stack width="100%">
      <TableContainer className={`tableContainerDrmt ${isOpenMenu ? "tableContainerOpened" : "tableContainerClosed"}`}>
        <Table stickyHeader aria-label="sticky table" className="drmt-table" {...getTableProps()} data-testid="table">
          <TableHead className="drmt-head">
            {headerGroups.map((headerGroup, i) => (
              <Fragment key={i}>
                <TableRow key={`filters-${i}`} className="drmt-filter-row">
                  {headerGroup.headers.map((column, j) => (
                    <TableCell key={`filter-${j}`} {...column.getHeaderProps()} style={{ width: column.width || "auto", minWidth: column.minWidth || "120px" }} className="drmt-table-cell">
                      <div className="drmt-div-filter">
                        {column.canFilter ? (
                          <div className="drmt-div-filter-child">{column.render("Filter")}</div>
                        ) : column.id === "selection" ? (
                          <div className="drmt-div-filter-con">{column.render("Filter")}</div>
                        ) : (
                          <div className="drmt-filter_align" />
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow {...headerGroup.getHeaderGroupProps()} key={`headers-${i}`} className="drmt-clean-line">
                  {headerGroup.headers.map((column, j) => (
                    <TableCell {...column.getHeaderProps()} key={`header-${j}`} className="drmt-table-header-cell" >
                      <div className="drmt-flex-align-center">
                        {column.id === "selection" ? (
                          column.render("Header")
                        ) : (
                        <>
                          <Typography variant="h2" className="drmt-tableHeader" data-testid={column.render("Header")}>
                            {t(column.render("Header"))}
                          </Typography>
                           {column?.color && <Typography className="drmt-tableHeader-require">*</Typography>}
                        </>
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            ))}
          </TableHead>

          <TableBody {...getTableBodyProps()} className="drmt-table-container-body">
            {isLoadingDrmTable ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="drmt-noRecodsRow">
                  <Typography variant="h3" data-testid="loading_records" className="drmt-noData">
                    {t("loading")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : page.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="noRecodsRow">
                  <Typography variant="h3" data-testid="no_data_to_display" className="noData">
                    {t("no_data_to_display")}
                  </Typography>                          
                </TableCell>
              </TableRow>
            ) : (
              page.map((row, index) => {
                prepareRow(row);
                const errorColumns = row.original?.errors || [];
                const isTmProcessing = row.original?.status === 5 || false;
                return (
                  <TableRow key={index} data-testid={`table-row-${index}`} className={clsx(row.isSelected ? "drmt-row-selected" : "row", tableState.density === "small" && "drmt-density-small", tableState.density === "medium" && "drmt-density-medium", tableState.density === "large" && "drmt-density-large", isTmProcessing && "drmt-row-disabled")} {...row.getRowProps()}>
                    {row.cells.map((cell, cellIndex) => {
                      let error_msg = "";
                      const isErrorColumn = errorColumns.some((error) => {
                        const accessor = cell.column.Header;
                        if (error.record == accessor) {
                          error_msg = error.errorMessage;
                          return true;
                        }
                      });
                      return (
                        <TableCell key={cellIndex} data-testid={`table-col-${cellIndex}`} {...cell.getCellProps()} className={clsx("drmt-bodyCell", isErrorColumn ? "errorCell" : "noErrorCell", tableState.density === "small" && "drmt-cell-small", tableState.density === "medium" && "drmt-cell-medium", tableState.density === "large" && "drmt-cell-large")} style={{ ...cell.getCellProps().style, color: isErrorColumn ? "red" : "inherit", minWidth: cell.column.minWidth || "120px", maxWidth: cell.column.maxWidth || "300px" }} title={isErrorColumn ? error_msg : typeof cell.value === "string" && cell.value.length > 50 ? cell.value : isErrorColumn ? error_msg : ""}>
                          <div className="drmt-text-container-cell">
                            {cell.render("Cell")}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <DialogRowError open={dialog.isOpen} handleCloseErrorDialog={handleCloseErrorDialog} errors={dialog.errors} />
      <div className="drmt-paginationDiv">
        <Pagination data-testid="pagination" className="drmt-pagination" count={totalPages} shape="rounded" page={onPage || 1} siblingCount={1} boundaryCount={1} onChange={handlePageChange} />

        <Stack className="drmt-rowsPerPage" direction="row" gap={1}>
          <Stack>
            {computedSelectedCount > 0 && (
              <Typography variant="h5" className="drmt-recordTextStyle">
                {computedSelectedCount} {t('selected_row')}
              </Typography>
            )}
          </Stack>
          <Stack>
            <Typography variant="h5" className="drmt-recordTextStyle">
              {t("displaying")} {startRecNum} - {endRecNum} of {totalRecord}
            </Typography>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
}

export default DrmTable;